import { Modal, Button, Spin, Space, message } from 'antd';
import { DownloadOutlined, PrinterOutlined } from '@ant-design/icons';
import { useEffect, useState } from 'react';

interface Props {
  open: boolean;
  onClose: () => void;
  title: string;
  // Deferred so the modal shows a spinner while the PDF is being fetched.
  // Called each time the modal opens; caller controls caching if needed.
  fetchBlob: () => Promise<Blob>;
  // Filename used by the Download button. Extension optional; .pdf appended
  // when missing.
  filename: string;
}

// Reusable inline PDF viewer. Uses an object URL + <iframe> so the browser's
// native PDF viewer handles zoom / page navigation / print for free.
// Alternative was react-pdf, but the bundle weight isn't worth it here —
// this modal is the entire UX we need.
export default function PdfPreviewModal({ open, onClose, title, fetchBlob, filename }: Props) {
  const [loading, setLoading] = useState(false);
  const [blob, setBlob] = useState<Blob | null>(null);
  const [url, setUrl] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    let objectUrl: string | null = null;
    setBlob(null);
    setUrl(null);
    setErr(null);
    setLoading(true);
    (async () => {
      try {
        const b = await fetchBlob();
        if (cancelled) return;
        objectUrl = URL.createObjectURL(b);
        setBlob(b);
        setUrl(objectUrl);
      } catch (e: any) {
        if (cancelled) return;
        const errBlob = e?.response?.data as Blob | undefined;
        if (errBlob && errBlob.type?.includes('json')) {
          try {
            const text = await errBlob.text();
            setErr(JSON.parse(text)?.error ?? text);
          } catch {
            setErr('Failed to load PDF');
          }
        } else {
          setErr(e?.message ?? 'Failed to load PDF');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [open, fetchBlob]);

  const download = () => {
    if (!blob) return;
    const dlUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = dlUrl;
    a.download = filename.endsWith('.pdf') ? filename : `${filename}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(dlUrl), 1000);
  };

  const print = () => {
    if (!url) return;
    // Native print — the iframe already has focus semantics from the viewer,
    // but opening in a hidden window is the cross-browser reliable path.
    const w = window.open(url, '_blank');
    if (w) {
      w.focus();
      // Chrome/Edge auto-open the print dialog for PDFs when you use the
      // #toolbar hash, but that's inconsistent. Leaving it as "opens the PDF
      // in a new tab where the user can Ctrl+P" is the reliable behavior.
    } else {
      message.warning('Popup blocked — allow popups for this site, then retry.');
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={title}
      width={960}
      styles={{ body: { padding: 0, height: '70vh', display: 'flex', flexDirection: 'column' } }}
      footer={
        <Space>
          <Button onClick={onClose}>Close</Button>
          <Button icon={<DownloadOutlined />} onClick={download} disabled={!blob}>
            Download
          </Button>
          <Button icon={<PrinterOutlined />} type="primary" onClick={print} disabled={!url}>
            Print
          </Button>
        </Space>
      }
    >
      {loading && (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Spin tip="Loading PDF..." />
        </div>
      )}
      {err && !loading && (
        <div style={{ padding: 24, color: '#ff4d4f' }}>{err}</div>
      )}
      {url && !loading && (
        <iframe
          title={title}
          src={url}
          style={{ flex: 1, width: '100%', border: 'none' }}
        />
      )}
    </Modal>
  );
}
